<template>
  <v-row>
    <Confirm
      :confirm.sync="confirmDelete"
      :callback="deleteAction"
      message="Are you sure you want to delete this action?"
    ></Confirm>
    <v-speed-dial
      v-model="fab"
      :absolute="true"
      :top="true"
      :right="true"
      direction="bottom"
    >
      <template v-slot:activator>
        <v-btn v-model="fab" color="primary" dark fab>
          <v-icon v-if="fab">mdi-close</v-icon>
          <v-icon v-else>mdi-arrow-right-circle</v-icon>
        </v-btn>
      </template>
      <v-btn fab light small @click="showList = !showList">
        <v-icon>mdi-view-list</v-icon>
      </v-btn>
      <v-btn fab light small @click="newAction()">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
      <v-btn
        fab
        dark
        small
        color="red"
        @click="confirmDelete = true"
        v-if="action && action.id"
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </v-speed-dial>
    <v-col class="px-6" cols="12" :md="showList ? 3 : 0" v-if="showList">
      <v-expansion-panels elevation="1" :value="0">
        <v-expansion-panel>
          <v-expansion-panel-header class="title">
            Actions
            <template v-slot:actions>
              <v-icon>mdi-alert-circle-outline</v-icon>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <p>
              Actions are automated responses to certain user input. Actions can
              be triggered by commands or by key/trigger words.
            </p>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-progress-linear
        indeterminate
        color="primary"
        v-if="actionsLoading"
        height="7"
      ></v-progress-linear>
      <v-data-iterator
        :items="actions"
        item-key="id"
        :loading="actionsLoading"
        :items-per-page.sync="limit"
        :server-items-length="totalActions"
        :page.sync="page"
        :footer-props="{
          'items-per-page-options': itemsPerPageArray,
          'items-per-page-text': '',
          'show-current-page': true,
        }"
        loading-text="Loading actions..."
        no-results-text="You have not created any actions yet."
      >
        <template v-slot:default="props">
          <v-list elevation="1">
            <v-list-item-group v-model="selected" color="primary">
              <v-list-item
                two-line
                v-for="action in props.items"
                :key="action._id"
              >
                <v-list-item-icon>
                  <span
                    class="text-h5"
                    v-if="action.properties && action.properties.icon"
                    >{{ action.properties && action.properties.icon }}</span
                  >
                  <v-icon
                    v-if="!action.properties || !action.properties.icon"
                    >{{ getActionIcon(action.trigger) }}</v-icon
                  >
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title>{{ action.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{
                    action.description
                  }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-icon>
                  <v-icon
                    color="grey"
                    :title="action.trigger.enabled ? 'Enabled' : 'Disabled'"
                    small
                    >{{
                      action.trigger.enabled
                        ? "mdi-arrow-right-circle"
                        : "mdi-arrow-right"
                    }}</v-icon
                  >
                </v-list-item-icon>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </template>
      </v-data-iterator>
    </v-col>
    <v-col class="px-6" cols="12" :md="showList ? 9 : 12">
      <Action
        :action="action"
        :elaborators="elaborators"
        :groups="groups"
        :features="enabledFeat"
        v-if="action"
      ></Action>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import { models, makeFindMixin, makeGetMixin } from "feathers-vuex";
import Action from "@/components/actions/Action";
import Confirm from "@/components/widget/Confirm";

export default {
  data: () => ({
    fab: null,
    showList: true,
    selected: null,
    action: null,
    confirmDelete: false,
    enabledFeaturesConfigId: "enabledFeatures",
    itemsPerPageArray: [5, 10, 20, 30, 50, -1],
    page: 1,
    limit: 10,
    skip: 0,
    pagination: {
      rowsPerPage: 5,
    },
    actionIcon: {
      command: "mdi-slash-forward",
      keywords: "mdi-key",
      media: "mdi-camera",
      special: {
        license_plate: "mdi-car-info",
        profanity: "mdi-exclamation",
      },
      schedule: "mdi-calendar-clock",
    },
  }),
  mixins: [
    makeFindMixin({ service: "actions", watch: true }),
    makeFindMixin({ service: "elaborators" }),
    makeFindMixin({ service: "groups" }),
    makeGetMixin({
      service: "configuration",
      name: "enabledFeat",
      id: "enabledFeaturesConfigId",
    }),
  ],
  async mounted() {
    this.newAction();
  },
  computed: {
    actionsLoading() {
      return this.isFindActionsPending;
    },
    totalActions() {
      return this.actionsPaginationData.default &&
        this.actionsPaginationData.default.mostRecent.total
        ? this.actionsPaginationData.default.mostRecent.total
        : 0;
    },
    actionsParams() {
      return {
        query: {
          $limit: this.limit !== -1 ? this.limit : this.totalActions,
          $skip: this.skip,
        },
      };
    },
    elaboratorsParams() {
      return { query: {} };
    },
    groupsParams() {
      return {
        query: {
          deleted: false,
        },
      };
    },
  },
  methods: {
    ...mapActions("app", ["showMessage"]),
    newAction() {
      const { Action } = models.bot;
      this.action = new Action();
    },
    getActionIcon(trigger) {
      if (trigger.type === "special") {
        return this.actionIcon.special[trigger.special];
      } else {
        return this.actionIcon[trigger.type];
      }
    },
    async deleteAction() {
      if (this.action && this.action.id) {
        try {
          await this.action.remove();
          this.showMessage({ text: "Action deleted!", color: "green" });
          this.newAction();
        } catch (error) {
          this.error = error;
          this.showMessage({
            text: error.message || "Error occured!",
            color: "red",
          });
        }
      }
    },
  },
  components: {
    Action,
    Confirm,
  },
  watch: {
    page(now) {
      this.skip = now * this.limit - this.limit;
      this.newAction();
      this.selected = null;
    },
    selected(item) {
      if (
        this.selected !== undefined &&
        this.selected !== null &&
        this.actions &&
        this.actions[item]
      ) {
        this.action = this.actions[item].clone();
      } else if (this.selected === undefined) {
        this.action = null;
      }
    },
    action(item) {
      if (
        this.selected !== undefined &&
        this.selected !== null &&
        this.actions &&
        this.actions[this.selected] &&
        this.actions[this.selected].id !== item.id
      ) {
        this.selected = null;
      } else if (
        (this.selected === undefined || this.selected === null) &&
        item &&
        item.id
      ) {
        this.selected = this.actions.length - 1;
      }
    },
  },
};
</script>